import Flex from "@/components/flex"
import TabButton from "@/components/buttons/tab-button"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { clockStore } from "./clock-store"
import { useRef } from "react"
import { RESOURCE_META } from "@/constants/resource-const"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { hexToRgb } from "../../units-util"
import { clockBundle, clockTabs } from "./clock-const"
import Wrapper from "@/components/wrapper"

const ClockView = ({ className = "" }) => {
  const { lang } = langStore()
  const [searchParams] = useSearchParams()

  const resource = searchParams.get("resource") ?? "water"
  const dong = searchParams.get("dong")
  const ho = searchParams.get("ho")

  const [period, setPeriod] = useState(7)
  const [hoveredHour, setHoveredHour] = useState(null)
  const centerHoverRef = useRef(false)
  const { datasets, peak } = clockStore()

  const currentTheme = useMemo(() => {
    return RESOURCE_META[resource] || RESOURCE_META["water"]
  }, [resource])

  useEffect(() => {
    if (!dong || !ho) return
    clockStore.getState().apiGetPatternClock({ dong, ho, resource })
  }, [resource, dong, ho])

  const selectedData = useMemo(() => {
    if (!datasets) return []
    if (period === 1) return datasets.today
    if (period === 7) return datasets.last7days
    return datasets.last30days
  }, [datasets, period])

  const peakData = useMemo(() => {
    if (!peak) return null
    if (period === 1) return peak.today
    if (period === 7) return peak.last7days
    return peak.last30days
  }, [peak, period])

  const peakHour = peakData?.hour ?? 0
  const nowHour = new Date().getHours()

  const periodMeta = useMemo(() => {
    if (period === 1) {
      return {
        subTitle: clockBundle.period.today[lang],
        centerSub: clockBundle.center.todayPeak[lang],
        valueLabel: clockBundle.center.todayUsage[lang]
      }
    }
    return {
      subTitle: formatMessage(clockBundle.period.recent[lang], { period }),
      centerSub: clockBundle.center.basePeak[lang],
      valueLabel: clockBundle.center.averageUsage[lang]
    }
  }, [period, lang])

  const radius = 72
  const circumference = 2 * Math.PI * radius
  const segmentLength = circumference / 24

  const renderedSegments = useMemo(() => {
    if (!selectedData.length) {
      return Array(24).fill("#F1F5F9")
    }

    const max = Math.max(...selectedData)
    const rgbStr = hexToRgb(currentTheme.colors.primary)

    return selectedData.map((value, idx) => {
      if (period === 1 && idx > nowHour) return "#F8FAFC"

      const ratio = max === 0 ? 0 : value / max
      const isHovered = hoveredHour === idx
      const opacity = isHovered ? 1.0 : 0.06 + ratio * 0.94

      return `rgba(${rgbStr},${opacity})`
    })
  }, [selectedData, period, nowHour, currentTheme, hoveredHour])

  return (
    <Wrapper.shadow className={className}>
      <Flex className="justify-between items-center mb-4 min-w-full md:min-w-110" gap={0}>
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {formatMessage(clockBundle.title[lang], { resource: currentTheme.label[lang] })}
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">{periodMeta.subTitle}</p>
        </div>

        <Wrapper.tab>
          {clockTabs.map((t) => (
            <TabButton
              key={t.value}
              onClick={() => setPeriod(t.value)}
              isActive={period === t.value}
            >
              {t[lang]}
            </TabButton>
          ))}
        </Wrapper.tab>
      </Flex>

      <div className="flex-1 flex flex-col items-center justify-center p-4 min-h-0 overflow-hidden">
        <div className="relative flex h-full aspect-square items-center justify-center">
          <svg
            className="w-full h-full -rotate-90 select-none max-h-full max-w-full"
            viewBox="0 0 192 192"
            preserveAspectRatio="xMidYMid meet"
          >
            {renderedSegments.map((color, idx) => (
              <circle
                key={idx}
                cx="96"
                cy="96"
                r={radius}
                fill="transparent"
                stroke={color}
                strokeWidth="46"
                strokeDasharray={`${segmentLength} ${circumference}`}
                strokeDashoffset={-segmentLength * idx}
                style={{ pointerEvents: "stroke" }}
                className="cursor-pointer transition-all duration-150"
                onMouseEnter={() => (!centerHoverRef.current ? setHoveredHour(idx) : null)}
                onMouseLeave={() => setHoveredHour(null)}
              />
            ))}
          </svg>

          <div
            onMouseEnter={() => {
              centerHoverRef.current = true
              setHoveredHour(null)
            }}
            onMouseLeave={() => (centerHoverRef.current = false)}
            className="absolute w-[56%] h-[56%] rounded-full bg-white shadow-md flex flex-col items-center justify-center text-center border border-gray-50/50 z-20"
          >
            {hoveredHour !== null ? (
              <>
                <span className="text-[9px] font-bold text-gray-400 tracking-wider">
                  {String(hoveredHour).padStart(2, "0")}:00 ~{" "}
                  {String((hoveredHour + 1) % 24).padStart(2, "0")}:00
                </span>
                <span
                  className="text-base font-black mt-0.5"
                  style={{ color: currentTheme.primary }}
                >
                  {selectedData[hoveredHour]?.toFixed(2) ?? 0}{" "}
                  <span className="text-[10px] font-normal">{currentTheme.unit}</span>
                </span>
                <span className="text-[9px] text-gray-400 mt-0.5 font-medium">
                  {periodMeta.valueLabel}
                </span>
              </>
            ) : (
              <>
                <span className="text-[9px] font-bold text-gray-400 tracking-wider">
                  {clockBundle.center.mainPeak[lang]}
                </span>
                <span
                  className="text-base font-extrabold mt-0.5"
                  style={{ color: currentTheme.primary }}
                >
                  {peakHour}
                  {clockBundle.center.hour[lang]}
                </span>
                <span className="text-[9px] text-gray-400 scale-95 mt-0.5">
                  {periodMeta.centerSub}
                </span>
              </>
            )}
          </div>

          <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-bold text-gray-400">
            00
          </span>
          <span className="absolute top-1/2 -right-4 -translate-y-1/2 text-[10px] font-bold text-gray-400">
            06
          </span>
          <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-[10px] font-bold text-gray-400">
            12
          </span>
          <span className="absolute top-1/2 -left-4 -translate-y-1/2 text-[10px] font-bold text-gray-400">
            18
          </span>
        </div>
      </div>
    </Wrapper.shadow>
  )
}

export default ClockView
